// The option's paint list in the rates block (the template's G10:N15, without
// its six-slot limit): each paint's sell and cost $/L, removing one, and
// adding a product from the price books by search. The first paint is the
// standard one sundries use, so it stays. A price-book paint's cost can be
// typed as a job price, and a one-off paint added for this job only (#413):
// both price every option on the measure and are tagged for the reviewer.
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { searchPaints } from '../lib/api';
import { oneOffPaintId, oneOffPaintKey, priceTag, type PaintChoice, type PaintRate } from '@higgins/m2q-pricing';
import type { JobPaintActions } from '../lib/job-prices';
import type { PricingRates } from '@higgins/m2q-pricing';
import type { PaintProductFromApi } from '../lib/types';
import { formatMoney, formatTwo } from './surface-format';
import { AddAction, AddActionLabel } from './add-action';
import { ErrorMessage } from './message';
import { NumberField } from './number-field';
import { SavedTextField, TEXT_MAX } from './saved-text-field';
import { CHANGED } from './take-off-rows';

const SEARCH_MIN_CHARS = 2;
const SEARCH_DELAY_MS = 300;

/** Price-book matches for what's typed, after a short pause; nothing under two characters. */
function usePaintSearch(text: string, date: string) {
  const [result, setResult] = useState<{ products: PaintProductFromApi[]; more: boolean; error?: string } | null>(null);
  useEffect(() => {
    const search = text.trim();
    if (search.length < SEARCH_MIN_CHARS) {
      setResult(null);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      searchPaints(search, date).then(
        (found) => current && setResult(found),
        () => current && setResult({ products: [], more: false, error: 'The price books could not be searched — check the signal.' }),
      );
    }, SEARCH_DELAY_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [text, date]);
  return result;
}

interface Props {
  paints: PaintChoice[];
  rates: PricingRates;
  date: string;
  onAdd: (paint: PaintChoice) => void;
  onRemove: (key: string) => void;
  job: JobPaintActions;
}

/** The cost cell: a price-book or one-off paint's job price can be typed; a generic paint shows its setting. */
function CostCell({ paint, rate, job }: { paint: PaintChoice; rate: PaintRate | undefined; job: JobPaintActions }) {
  const oneOff = oneOffPaintId(paint.key);
  if (oneOff) {
    return <NumberField compact value={rate?.costPerLitre ?? null} onChange={(v) => job.onOneOff(oneOff, { hig_costperlitre: v })} label={`${paint.name} cost per litre`} className={`w-16 ${CHANGED}`} />;
  }
  if (!paint.productId) return <>{formatTwo(rate?.costPerLitre ?? 0)}</>;
  const jobPrice = rate?.priceSource === 'job' ? rate.costPerLitre : null;
  const book = rate?.bookCostPerLitre ?? null;
  return (
    <NumberField
      compact
      value={jobPrice}
      placeholder={formatTwo(book ?? rate?.costPerLitre ?? 0)}
      onChange={(v) => job.onJobPrice(paint, v)}
      label={`${paint.name} job price per litre`}
      className={`w-16 ${jobPrice === null ? '' : CHANGED}`}
    />
  );
}

/** A one-off paint (name + $/L ex GST) for this job only, added once both are typed. */
function OneOffForm({ onAdd }: { onAdd: (name: string, cost: number) => void }) {
  const [name, setName] = useState('');
  const [cost, setCost] = useState<number | null>(null);
  const ready = name.trim() !== '' && cost !== null && cost > 0;
  function add() {
    if (!ready) return;
    onAdd(name.trim(), cost);
    setName('');
    setCost(null);
  }
  return (
    <div className="mt-1 flex items-center gap-1 text-xs">
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={TEXT_MAX} placeholder="One-off paint (this job only)" aria-label="One-off paint name" className="h-7 min-w-0 flex-1 rounded-md border border-slate-300 px-1 text-xs" />
      <NumberField compact value={cost} placeholder="$/L" onChange={setCost} label="One-off paint cost per litre, ex GST" className="w-14 rounded-md border border-slate-300" />
      <AddAction onClick={add} disabled={!ready} className="px-1">
        Add
      </AddAction>
    </div>
  );
}

export function PaintList({ paints, rates, date, onAdd, onRemove, job }: Props) {
  const [text, setText] = useState('');
  const found = usePaintSearch(text, date);
  const markup = 1 + rates.markupMaterials;
  const rateOf = new Map(rates.paints.map((p) => [p.id, p]));
  const keys = new Set(paints.map((p) => p.key));
  const unusedOneOffs = job.oneOffs.filter((p) => !keys.has(oneOffPaintKey(p.id)));

  function add(product: PaintProductFromApi) {
    onAdd({ key: product.id, name: `${product.name}${product.supplier ? ` (${product.supplier})` : ''}`, productId: product.id });
    setText('');
  }

  return (
    <div>
      <table className="w-full">
        <tbody>
          {paints.map((p, i) => {
            const rate = rateOf.get(p.key);
            const cost = rate?.costPerLitre ?? 0;
            const oneOff = oneOffPaintId(p.key);
            const tag = rate ? priceTag(rate) : null;
            return (
              <tr key={p.key}>
                <td className="py-0.5 pr-1 text-slate-600">
                  {i > 0 && (
                    <button type="button" onClick={() => onRemove(p.key)} className="mr-1 text-slate-400 hover:text-red-700 outdoor:min-h-11 outdoor:min-w-11" aria-label={`Remove ${p.name}`} title="Remove from this option">
                      <X className="inline h-3 w-3" />
                    </button>
                  )}
                  {oneOff ? (
                    <SavedTextField compact value={rate?.name ?? p.name} onSave={(name) => job.onOneOff(oneOff, { hig_name: name })} maxLength={TEXT_MAX} label="One-off paint name" className="w-28" />
                  ) : (
                    p.name
                  )}
                  {tag && <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-900">{tag}</span>}
                </td>
                <td className="py-0.5 text-right tabular-nums">{formatTwo(cost * markup)}</td>
                <td className="py-0.5 pl-3 text-right tabular-nums text-slate-500">
                  <CostCell paint={p} rate={rate} job={job} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {unusedOneOffs.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1 text-xs">
          {unusedOneOffs.map((p) => (
            <AddAction key={p.id} onClick={() => onAdd({ key: oneOffPaintKey(p.id), name: p.name })} className="px-1">
              {p.name} <span className="text-xs font-normal text-slate-500">(one-off{p.costPerLitre === null ? '' : `, ${formatMoney(p.costPerLitre)}/L`})</span>
            </AddAction>
          ))}
        </div>
      )}
      <OneOffForm onAdd={job.onAddOneOff} />
      <label className="mt-1 flex items-center gap-2">
        <AddActionLabel>Add paint</AddActionLabel>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search the price book…" aria-label="Search the price books" className="h-7 w-full rounded-md border border-slate-300 px-1 text-xs text-slate-800" />
      </label>
      {found && (
        <ul className="mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white text-xs">
          {found.error && <li className="p-2"><ErrorMessage>{found.error}</ErrorMessage></li>}
          {!found.error && found.products.length === 0 && <li className="p-2 text-slate-500">No active product matches.</li>}
          {found.products.map((product) => (
            <li key={product.id}>
              <button type="button" onClick={() => add(product)} className="flex w-full justify-between gap-2 px-2 py-1 text-left hover:bg-orange-50">
                <span>
                  {product.name} <span className="text-slate-500">{[product.supplier, product.supplierCode].filter(Boolean).join(' · ')}</span>
                </span>
                <span className="tabular-nums">{formatMoney(product.costPerLitre)}/L</span>
              </button>
            </li>
          ))}
          {found.more && <li className="p-2 text-slate-500">More match — type more of the name or code.</li>}
        </ul>
      )}
    </div>
  );
}
